// Configuración del semáforo de rentabilidad — Inventario → Configuración.
// Ruta: /inventario/rentabilidad-config. Los umbrales colorean el margen en
// Recepción (asistente), Reposición y el POS. Guardar exige nivel_acceso >= 5
// (inv_rentabilidad_config_set): si el usuario no es admin, PostgREST devuelve
// 403 y se muestra un aviso sin romper la pantalla.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  Campo, Categoria, CategoriaMargen, REDONDEO_OPCIONES, RedondeoEstilo, RentabilidadConfigData,
  Switch, fieldCls, inv, invRpc, num, toNum,
} from './shared';

export function RentabilidadConfig() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<RentabilidadConfigData>({
    queryKey: ['inv-rentabilidad-config'],
    queryFn: () => invRpc('inv_rentabilidad_config_get'),
  });

  const [rojo, setRojo] = useState('0');
  const [ambar, setAmbar] = useState('0');
  const [conDesc, setConDesc] = useState(true);
  const [peor, setPeor] = useState(true);
  const [margenObjetivo, setMargenObjetivo] = useState('0');
  const [redondeo, setRedondeo] = useState<RedondeoEstilo>('99');
  const [mayoristaRef, setMayoristaRef] = useState('0');
  const [sinPermiso, setSinPermiso] = useState(false);

  useEffect(() => {
    if (!data) return;
    setRojo(String(data.umbral_rojo));
    setAmbar(String(data.umbral_ambar));
    setConDesc(!!data.evaluar_con_descuento);
    setPeor(!!data.usa_peor_local);
    setMargenObjetivo(String(data.margen_objetivo));
    setRedondeo(data.redondeo_estilo);
    setMayoristaRef(String(data.mayorista_desc_ref));
  }, [data]);

  const invalido = Number(rojo) >= Number(ambar);

  const guardar = useMutation({
    mutationFn: () => invRpc('inv_rentabilidad_config_set', {
      _rojo: toNum(rojo), _ambar: toNum(ambar), _con_desc: conDesc, _peor: peor,
      _margen_obj: toNum(margenObjetivo), _redondeo: redondeo, _mayorista_ref: toNum(mayoristaRef),
    }),
    onSuccess: () => {
      setSinPermiso(false);
      toast.success('Configuración guardada');
      void qc.invalidateQueries({ queryKey: ['inv-rentabilidad-config'] });
    },
    onError: (e) => {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      if (status === 403) {
        setSinPermiso(true);
        toast.error('Requiere administrador');
      } else {
        toast.error(getErrorMessage(e));
      }
    },
  });

  return (
    <div className="max-w-2xl space-y-4 p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold"><Gauge className="w-5 h-5" /> Semáforo de rentabilidad</h1>
      <p className="text-sm text-muted-foreground">
        Configura los umbrales de margen que colorean el semáforo de rentabilidad en Recepción,
        Reposición y el buscador del POS.
      </p>

      {sinPermiso && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          Requiere administrador. Pide a un usuario con nivel de acceso 5 que guarde este cambio.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Umbrales de margen</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Umbral rojo (%) — por debajo es ROJO">
              <input type="number" step="0.1" className={fieldCls} value={rojo} onChange={(e) => setRojo(e.target.value)} />
            </Campo>
            <Campo label="Umbral ámbar (%) — por debajo es ÁMBAR">
              <input type="number" step="0.1" className={fieldCls} value={ambar} onChange={(e) => setAmbar(e.target.value)} />
            </Campo>
          </div>
          {invalido && <p className="text-xs text-red-600 dark:text-red-400">El umbral rojo debe ser menor que el umbral ámbar.</p>}

          <div className="space-y-2 border-t pt-3">
            <Switch checked={conDesc} onChange={setConDesc} label="Evaluar con el descuento del local (letra de precio)" />
            <Switch checked={peor} onChange={setPeor} label="Usar el peor local para el semáforo" />
          </div>

          <div className="grid grid-cols-2 gap-3 border-t pt-3 md:grid-cols-3">
            <Campo label="Margen objetivo de la empresa (%)">
              <input type="number" step="0.1" min="0" className={fieldCls} value={margenObjetivo} onChange={(e) => setMargenObjetivo(e.target.value)} />
            </Campo>
            <Campo label="Estilo de redondeo del PVP">
              <select className={fieldCls} value={redondeo} onChange={(e) => setRedondeo(e.target.value as RedondeoEstilo)}>
                {REDONDEO_OPCIONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Campo>
            <Campo label="Descuento mayorista referencial (%)">
              <input type="number" step="0.1" min="0" className={fieldCls} value={mayoristaRef} onChange={(e) => setMayoristaRef(e.target.value)} />
            </Campo>
          </div>
          <p className="text-xs text-muted-foreground">
            El margen objetivo y el redondeo alimentan la <b>sugerencia de precio</b> del asistente de Recepción;
            el descuento mayorista referencial se usa cuando activas el toggle «Mayorista» ahí mismo.
          </p>

          <div className="rounded-md border bg-muted/20 p-3 text-xs">
            <p className="mb-1.5 font-medium">Ejemplo con estos umbrales:</p>
            <ul className="space-y-1">
              <li className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-red-500" />
                Margen &lt; {num1(rojo)}% → <b>ROJO</b>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-amber-500" />
                Margen &lt; {num1(ambar)}% → <b>ÁMBAR</b>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-emerald-500" />
                Margen ≥ {num1(ambar)}% → <b>VERDE</b>
              </li>
            </ul>
          </div>

          <div className="flex justify-end border-t pt-3">
            <Button isLoading={guardar.isPending} disabled={isLoading || invalido} onClick={() => guardar.mutate()}>
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>

      <MargenPorFamilia />
    </div>
  );
}

const num1 = (v: string) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// ---- Margen objetivo por familia (override de categoría) -------------------
function MargenPorFamilia() {
  const qc = useQueryClient();
  const { data: overrides = [], isLoading } = useQuery<CategoriaMargen[]>({
    queryKey: ['inv-categoria-margen'],
    queryFn: () => invRpc('inv_categoria_margen_get'),
  });
  const { data: categorias = [] } = useQuery<Categoria[]>({
    queryKey: ['inv-categorias'],
    queryFn: () => inv.list<Categoria>('categorias', '?select=*&es_activo=eq.true&order=nombre'),
  });
  const [categoriaId, setCategoriaId] = useState('');
  const [margen, setMargen] = useState('');
  const [sinPermiso, setSinPermiso] = useState(false);

  const guardar = useMutation({
    mutationFn: (p: { categoria: string; margen: number }) =>
      invRpc('inv_categoria_margen_set', { _categoria: p.categoria, _margen: p.margen }),
    onSuccess: () => {
      setSinPermiso(false);
      toast.success('Margen por familia actualizado');
      setCategoriaId(''); setMargen('');
      void qc.invalidateQueries({ queryKey: ['inv-categoria-margen'] });
    },
    onError: (e) => {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      if (status === 403) { setSinPermiso(true); toast.error('Requiere administrador'); } else toast.error(getErrorMessage(e));
    },
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Margen objetivo por familia</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Un margen objetivo distinto al de la empresa para una categoría puntual. Cuando un producto pertenece
          a esta familia, la sugerencia de precio de Recepción usa este margen en vez del de la empresa.
        </p>

        {sinPermiso && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            Requiere administrador. Pide a un usuario con nivel de acceso 5 que guarde este cambio.
          </div>
        )}

        <div className="overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="py-2 px-2">Categoría</th>
                <th className="py-2 px-2 text-right">Margen objetivo %</th>
                <th className="py-2 px-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={3} className="py-4 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && overrides.length === 0 && (
                <tr><td colSpan={3} className="py-4 text-center text-muted-foreground">Sin overrides — todas las familias usan el margen de la empresa.</td></tr>
              )}
              {overrides.map((o) => (
                <tr key={o.categoria_id} className="border-b last:border-0">
                  <td className="py-1.5 px-2">{o.categoria}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{num(o.margen_objetivo, 1)}%</td>
                  <td className="py-1.5 px-2 text-right">
                    <button type="button" className="text-xs text-red-600 hover:underline"
                      onClick={() => guardar.mutate({ categoria: o.categoria_id, margen: 0 })}>
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <Campo label="Categoría">
            <select className={`${fieldCls} h-9 w-64`} value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Margen objetivo %">
            <input type="number" step="0.1" min="0" className={`${fieldCls} h-9 w-32`} value={margen} onChange={(e) => setMargen(e.target.value)} />
          </Campo>
          <Button size="sm" isLoading={guardar.isPending} disabled={!categoriaId || !margen || Number(margen) <= 0}
            onClick={() => guardar.mutate({ categoria: categoriaId, margen: toNum(margen) })}>
            Guardar override
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
